import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Truck, User } from "lucide-react";

export default function Auth() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "register" ? "register" : "login");
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("private");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");

  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const doLogin = async () => {
    setLoading(true);
    try { await login(email, password); toast.success("Bentornato!"); navigate("/dashboard"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doRegister = async () => {
    if (!name || !email || !password) { toast.error("Compila tutti i campi obbligatori"); return; }
    setLoading(true);
    try {
      await register({ email, password, name, role, phone, company: role === "carrier" ? company : null });
      toast.success("Account creato!"); navigate("/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* left panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12 relative overflow-hidden">
        <div className="grid-lines absolute inset-0 opacity-20" />
        <div className="relative"><Logo size={34} dark /></div>
        <div className="relative">
          <h2 className="font-display font-extrabold tracking-tighter text-4xl leading-tight">Spazio vuoto,<br />valore pieno.</h2>
          <p className="text-primary-foreground/70 mt-4 max-w-sm">Ottimizza i viaggi a vuoto dei mezzi di carico. Privati e professionisti, insieme.</p>
        </div>
        <div className="relative text-xs text-primary-foreground/50 font-mono-plex">© {new Date().getFullYear()} Truck Sharing</div>
      </div>

      {/* form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo size={30} /></div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 rounded-sm w-full">
              <TabsTrigger value="login" data-testid="tab-login">Accedi</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Registrati</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4 mt-6">
              <Field label="Email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm" data-testid="login-email-input" /></Field>
              <Field label="Password"><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm" data-testid="login-password-input" /></Field>
              <Button onClick={doLogin} disabled={loading} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" data-testid="login-submit-btn">
                {loading ? "Accesso…" : "Accedi"}
              </Button>
              <GoogleBtn onClick={googleLogin} />
            </TabsContent>

            <TabsContent value="register" className="space-y-4 mt-6">
              <div className="grid grid-cols-2 gap-3">
                <RoleCard active={role === "private"} onClick={() => setRole("private")} icon={User} label="Privato" testid="role-private" />
                <RoleCard active={role === "carrier"} onClick={() => setRole("carrier")} icon={Truck} label="Trasportatore" testid="role-carrier" />
              </div>
              <Field label="Nome completo *"><Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="register-name-input" /></Field>
              {role === "carrier" && <Field label="Ragione sociale"><Input value={company} onChange={(e) => setCompany(e.target.value)} className="rounded-sm" data-testid="register-company-input" /></Field>}
              <Field label="Email *"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm" data-testid="register-email-input" /></Field>
              <Field label="Telefono"><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-sm" data-testid="register-phone-input" /></Field>
              <Field label="Password * (min 6 caratteri)"><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm" data-testid="register-password-input" /></Field>
              <Button onClick={doRegister} disabled={loading} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" data-testid="register-submit-btn">
                {loading ? "Creazione…" : "Crea account"}
              </Button>
              <GoogleBtn onClick={googleLogin} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

function RoleCard({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`flex flex-col items-center gap-2 p-4 rounded-sm border transition-colors ${active ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/50"}`}>
      <Icon size={22} /><span className="text-sm font-medium">{label}</span>
    </button>
  );
}

function GoogleBtn({ onClick }) {
  return (
    <>
      <div className="relative py-1"><div className="border-t border-border" /><span className="absolute inset-0 flex justify-center -top-2.5"><span className="bg-background px-3 text-xs text-muted-foreground">oppure</span></span></div>
      <Button variant="outline" onClick={onClick} className="w-full rounded-sm gap-2" data-testid="google-login-btn">
        <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
        Accedi con Google
      </Button>
    </>
  );
}
